import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { fetchSettingsJson, getMobileSource, getVariants } from '../assets/helper';

/* eslint-disable react/jsx-props-no-spreading */
import { FunctionalComponent } from 'preact';
import PreviewSvg from '../assets/preview-bg.svg';
import Header from '../components/Header';
import MobileDevice from '../components/MobileDevie';


export enum VIEW {
  CPRA = 'cpra', // California Privacy Rights Act
  VCDPA = 'vcdpa', // Virginia Consumer Data Protection Act
  CPA = 'cpa', // Colorado Privacy Act
  CIPA = 'cipa', // California Information Privacy Act
  CTDPA = 'ctdpa', // Connecticut Data Privacy Act
  UCPA = 'ucpa', // Utah Consumer Privacy Act
  CCPA = 'ccpa',
  GDPR = 'gdpr',
  TCF = 'tcf',
  TCF2 = 'tcf2',
  MSPL = 'mspl',
  PIPEDA = 'pipeda', // Personal Information Protection and Electronic Documents Act
  DPDPA = 'dpdpa',
  ICDPA = 'icdpa',
  MCDP = 'mcdp',
  MCDPA = 'mcdpa',
  NDPA = 'ndpa',
  OCPA = 'ocpa',
  TIPA = 'tipa',
  NJDPA = 'njdpa',
  NHDPA = 'nhdpa',
  TDPSA = 'tdpsa',
  FDBR = 'fdbr',
  MODPA = 'modpa',
}

/** Views that are shown using the GDPR UI variant */
const GDPR_FALLBACK_VIEWS = new Set<VIEW>([
  VIEW.CIPA,
  VIEW.PIPEDA
]);

/** Views that are shown using the CCPA UI variant */
const CCPA_FALLBACK_VIEWS = new Set<VIEW>([
  VIEW.MSPL,
  VIEW.DPDPA,
  VIEW.ICDPA,
  VIEW.MCDP,
  VIEW.MCDPA,
  VIEW.NDPA,
  VIEW.OCPA,
  VIEW.TIPA,
  VIEW.NJDPA,
  VIEW.NHDPA,
  VIEW.TDPSA,
  VIEW.FDBR,
  VIEW.MODPA,
]);

/** Maps a display view to the script view the CMP loader expects */
const toScriptView = (view: VIEW | undefined): VIEW | undefined => {
  if (!view) return undefined;
  if (view === VIEW.TCF2) return VIEW.TCF;
  if (CCPA_FALLBACK_VIEWS.has(view)) return VIEW.CCPA;
  if (GDPR_FALLBACK_VIEWS.has(view)) return VIEW.GDPR;
  return view;
};

export enum DEVICE {
  DESKTOP = 'desktop',
  IOS = 'ios',
  ANDROID = 'android',
}

export enum CMP_VERSION {
  V2 = '2',
  V3 = '3',
}

type PreviewProps = {
  abVariant?: string;
  bundleSource?: string;
  device?: DEVICE;
  legacy?: boolean;
  sandbox?: boolean;
  cmpVersion?: CMP_VERSION;
  settingsId?: string;
  view?: VIEW;
  version?: string;
};

type CurrentView = {
  view?: VIEW;
};

const Preview: FunctionalComponent<PreviewProps> = ({
  abVariant,
  bundleSource,
  device = DEVICE.DESKTOP,
  cmpVersion,
  sandbox = false,
  settingsId = '',
  legacy,
  view,
  version = 'latest',
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalThis = global as any;
  const [settings, setSettings] = useState<{
    framework?: string;
    isCcpaEnabled: boolean;
    isTcfEnabled: boolean;
    selectedSettingsAppVersion: string;
    variants: string[];
    showOnlyCcpa: boolean;
    dsrConfigurationId: string | undefined;
  }>({
    isCcpaEnabled: false,
    showOnlyCcpa: false,
    isTcfEnabled: false,
    selectedSettingsAppVersion: 'V2',
    variants: [],
    dsrConfigurationId: undefined,
  });
  const passedView = view?.toLowerCase() as VIEW | undefined;
  let initialView = passedView;

  if (passedView === VIEW.TCF2) {
    initialView = VIEW.TCF;
  }

  const [availableViews, setAvailableViews] = useState<VIEW[]>([]);
  const [availableCmpVersions, setAvailableCmpVersions] = useState<CMP_VERSION[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentView, setCurrentView] = useState<CurrentView>({ view: initialView });
  const [currentDevice, setCurrentDevice] = useState<DEVICE>(device);
  const [currentCmpVersion, setCurrentCmpVersion] = useState<CMP_VERSION | undefined>(cmpVersion);
  const [currentAbVariant, setCurrentAbVariant] = useState<string | undefined>(abVariant);
  const [isAgeVerificationEnabled, setIsAgeVerificationEnabled] = useState(false);

  const isEdge = window.navigator.userAgent.indexOf('Edge') !== -1;
  const isIE = window.navigator.userAgent.indexOf('Trident') !== -1 && !isEdge;
  const isLegacy = legacy || isIE;
  let scriptPath = 'https://app.usercentrics.eu/browser-ui/latest/';
  let useSandbox = sandbox;
  const { href } = window.location;

  if (bundleSource === 'integration') {
    useSandbox = true;
  } else if (bundleSource === 'local') {
    scriptPath = 'http://localhost:1111/';
  } else {
    const urlMatchRegex = new RegExp('/(develop|staging|test/EUD-[0-9]+|pr/[0-9]+)/');
    const bundleSourceRegex = new RegExp('^(develop|staging|test/EUD-[0-9]+|pr/[0-9]+)$');
    const domainSource = bundleSource === 'staging' ? 'staging' : 'develop';

    if (!bundleSource && urlMatchRegex.test(href)) {
      const env = href.match(urlMatchRegex);

      if (env) {
        scriptPath = `https://app.usercentrics-${domainSource}.eu/${env[1]}/`;
      }
    } else if (bundleSource && bundleSourceRegex.test(bundleSource)) {
      scriptPath = `https://app.usercentrics-${domainSource}.eu/${bundleSource}/`;
    }
  }

  const updateUrlParam = (
    param: 'view' | 'device' | 'cmpVersion' | 'abVariant', 
    value: VIEW | DEVICE | CMP_VERSION | string,
    remove?: boolean,
  ) => {
    const searchParams = new URLSearchParams(window.location.search);
    if (remove) {
      searchParams.delete(param);
    } else {
      searchParams.set(param, value);
    } 
    window.location.replace(`?${searchParams.toString()}`);
  };

  const navigateToView = (viewParam: VIEW) => {
    updateUrlParam('view', viewParam);
  };

  /**
   * INIT FUNCTIONS
   */
  useEffect(() => {
    if (isInitialized) {
      const updatedAvailableViews = [];
      if (settings.framework) {
        const framework = settings.framework.toLowerCase() as VIEW;
        updatedAvailableViews.push(framework === VIEW.TCF2 ? VIEW.TCF : framework);
      } else {
        if (settings.isTcfEnabled) {
          updatedAvailableViews.push(VIEW.TCF);
        } else {
          if (settings.isCcpaEnabled) {
            updatedAvailableViews.push(VIEW.CCPA);
          }
          if (!settings.showOnlyCcpa) {
            updatedAvailableViews.push(VIEW.GDPR);
          }
        }
      }
      setAvailableViews(updatedAvailableViews);

      const updatedCmpVersions = [];
      if (settings.selectedSettingsAppVersion === 'V2') {
        updatedCmpVersions.push(CMP_VERSION.V2, CMP_VERSION.V3);
      } else {
        updatedCmpVersions.push(CMP_VERSION.V3);
      }
      setAvailableCmpVersions(updatedCmpVersions);

      if (!currentView.view) {
        setCurrentView({ view: updatedAvailableViews[0] });
      }

      if (!currentCmpVersion) {
        setCurrentCmpVersion(updatedCmpVersions[0]);
      }
    }
  }, [settings, settingsId, isInitialized]);

  const initPreview = useCallback(() => {
    if (typeof globalThis.UC_UI !== 'undefined' && globalThis.UC_UI && globalThis.UC_UI.isInitialized) {
      if (document.referrer.includes('admin.usercentrics.com')) {
        globalThis.UC_UI.clearStorage();
      }
    }

    fetchSettingsJson(settingsId, version, 'en', useSandbox).then((data) => {
      setSettings({
        isCcpaEnabled: data.ccpa.isActive,
        isTcfEnabled: data.tcf2Enabled,
        framework: data.framework || undefined,
        variants: data && data.variants ? getVariants(data.variants) : [],
        showOnlyCcpa: data.ccpa.isActive && data.ccpa.region === 'ALL',
        selectedSettingsAppVersion: data.selectedSettingsAppVersion || 'V2',
        dsrConfigurationId: data.dsrConfigurationId,
      });
      setIsInitialized(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalThis.UC_UI, settingsId, version]);

  useEffect(() => {
    if (!isInitialized) {
      initPreview();
    }
  }, [device, initPreview, isInitialized]);

  useEffect(() => {
    if (device !== currentDevice) {
      updateUrlParam('device', currentDevice);
    }
  }, [device, currentDevice]);

  useEffect(() => {
    if (isInitialized && currentCmpVersion && cmpVersion !== currentCmpVersion) {
      try {
        if (currentCmpVersion === CMP_VERSION.V3) {
          window['__ucCmp']?.clearUserSession();
        } else {
          window['UC_UI']?.clearStorage();
        }
      } catch (error) {
        console.warn('Not able to clean user session', error);
      }
      updateUrlParam('cmpVersion', currentCmpVersion);
    }
  }, [cmpVersion, currentCmpVersion, isInitialized]);

  const applyAgeVerificationStatusRef = useRef<() => Promise<void>>(async () => {});
  const stableHandler = useCallback(() => {
    applyAgeVerificationStatusRef.current();
  }, []);

  useEffect(() => {
    if (!isInitialized || currentCmpVersion !== CMP_VERSION.V3) {
      setIsAgeVerificationEnabled(false);
      return;
    }
    let cancelled = false;
    const applyAgeVerificationStatus = async () => {
      if (cancelled) return;
      try {
        const required = await window.__ucCmp?.isAgeVerificationEnabled?.();
        if (!cancelled) {
          setIsAgeVerificationEnabled(!!required);
        }
      } catch {
        if (!cancelled) {
          setIsAgeVerificationEnabled(false);
        }
      }
    };
    applyAgeVerificationStatusRef.current = applyAgeVerificationStatus;

    if (window.__ucCmp?.isAgeVerificationEnabled) {
      applyAgeVerificationStatus();
    }
    window.addEventListener('UC_UI_INITIALIZED', stableHandler);

    return () => {
      cancelled = true;
      window.removeEventListener('UC_UI_INITIALIZED', stableHandler);
    };
  }, [isInitialized, currentCmpVersion, stableHandler]);


  /**
   * UI HANDLERS
   */
  const onViewChange = (v: any) => {
    if (v && Object.values(VIEW).includes(v.toLowerCase())) {
      navigateToView(v.toLowerCase());
    }
  };

  const onTryOtherDevices = () => {
    if (device !== DEVICE.DESKTOP) {
      const scriptView = toScriptView(currentView.view);
      window.open(
        getMobileSource(
          device,
          {
            location: scriptView !== VIEW.GDPR && scriptView !== VIEW.TCF ? 'US-CA' : 'DE-BY',
            settingsId,
            version,
          },
          useSandbox,
          'app',
        ),
        '_blank',
      );
    }
  };

  const onShowFirstLayer = () => {
    if (device === DEVICE.DESKTOP) {
      globalThis.UC_UI.showFirstLayer();
    }
  };

  const onShowSecondLayer = () => {
    if (device === DEVICE.DESKTOP) {
      globalThis.UC_UI.showSecondLayer();
    }
  };

  const onRefreshPreview = async () => {
    if (device === DEVICE.DESKTOP) {
      globalThis.localStorage.removeItem('uc_ab_variant');
      await globalThis.UC_UI.clearStorage();
      window.location.reload();
    }
  };

  const onShowAVGate = () => {
    if (device === DEVICE.DESKTOP && isAgeVerificationEnabled) {
      window.__ucCmp?.clearUserSession()
        .then(() => { globalThis.UC_UI?.showFirstLayer(); })
        .catch((err: unknown) => { console.error('[AV gate] clearUserSession failed:', err); });
    }
  };

  const onOpenDsrForm = () => {
    if (!settings.dsrConfigurationId) return;
    const baseUrl = useSandbox ? 'https://dsr.usercentrics-dev.io' : 'https://dsr.usercentrics.eu';
    const isDraft = version === 'preview';
    const url = `${baseUrl}/dsr/${settings.dsrConfigurationId}${isDraft ? '?draft=true' : ''}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onVariantChange = (abVariant: string) => {
    if (abVariant === 'A/B Test Variant') {
      setCurrentAbVariant(undefined);
      delete globalThis.UC_AB_VARIANT;
      localStorage.removeItem('uc_ab_variant');
      updateUrlParam('abVariant', '', true);
      return;
    }
    setCurrentAbVariant(abVariant);
    globalThis.UC_AB_VARIANT = abVariant;
    localStorage.setItem('uc_ab_variant', abVariant);
    updateUrlParam('abVariant', abVariant);
  };

  const onLogoClick = () => {
    window.location.reload();
  };

  if (!currentView.view) return null;
  const scriptView = toScriptView(currentView.view);

  return (
    <div id="preview">
      {scriptView !== VIEW.GDPR && scriptView !== VIEW.TCF ? (
        <script>
          {`
            window.UC_UI_USER_COUNTRY_DATA = {
              code: 'US',
              name: 'United States of America',
              regionCode: 'CA'
            }
          `}
        </script>
      ) : (
        <script>
          {`
            window.UC_UI_USER_COUNTRY_DATA = {
              code: 'DE',
              name: 'Germany',
              regionCode: 'BY'
            }
        `}
        </script>
      )}
      {device === DEVICE.DESKTOP && currentCmpVersion === CMP_VERSION.V2 && (
        <script
          id="usercentrics-cmp"
          data-disable-tracking
          data-settings-id={settingsId}
          data-settings-cache={Date.now()}
          data-version={version}
          src={`${scriptPath}bundle${isLegacy ? '_legacy' : ''}.js`}
          {...(useSandbox && { 'data-sandbox': true })}
          {...(scriptView === VIEW.TCF && { 'data-tcf-enabled': true })}
        />
      )}
      {device === DEVICE.DESKTOP && currentCmpVersion === CMP_VERSION.V3 && (
        <script
          id="usercentrics-cmp"
          data-disable-tracking
          data-legislation-view={scriptView}
          data-settings-id={settingsId}
          src={
            bundleSource === 'local'
              ? `${scriptPath}loader.js`
              : useSandbox
                ? 'https://web.cmp.usercentrics-sandbox.eu/ui/loader.js'
                : 'https://web.cmp.usercentrics.eu/ui/loader.js'
          }
          {...(useSandbox && { 'data-sandbox': true })}
          {...(version === 'preview' && { 'data-draft': true })}
          {...(abVariant && { 'data-ab-variant': abVariant })}
        />
      )}
      <Header
        availableVariants={settings.variants}
        availableViews={availableViews}
        currentView={currentView.view}
        currentVariant={currentAbVariant}
        device={device}
        version={version}
        availableCmpVersions={availableCmpVersions}
        cmpVersion={currentCmpVersion}
        isInitialized={isInitialized}
        isAgeVerificationEnabled={isAgeVerificationEnabled}
        isDsrEnabled={!!settings.dsrConfigurationId}
        onChangeDevice={setCurrentDevice}
        onCmpVersionChange={setCurrentCmpVersion}
        onLogoClick={onLogoClick}
        onShowFirstLayer={onShowFirstLayer}
        onShowSecondLayer={onShowSecondLayer}
        onRefreshPreview={onRefreshPreview}
        onShowAVGate={onShowAVGate}
        onOpenDsrForm={onOpenDsrForm}
        onVariantChange={onVariantChange}
        onViewChange={onViewChange}
        onTryOtherDevices={onTryOtherDevices}
      />

      {device === DEVICE.DESKTOP ? (
        <img src={PreviewSvg} alt="preview" style={{ width: '100vw' }} />
      ) : (
        <MobileDevice
          useSandbox={useSandbox}
          device={device}
          urlParams={{
            location: scriptView !== VIEW.GDPR && scriptView !== VIEW.TCF ? 'US-CA' : 'DE-BY',
            settingsId,
            version,
          }}
        />
      )}
    </div>
  );
};

export default Preview;
